import { useMutation } from '@tanstack/react-query';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../constants/firebaseModular';
import { ContactEmailUpdate } from '../types/Discounts/ContactEmailUpdate';
import { EnrollmentInfo } from '../types/Patient/EnrollmentInfo';
import { NavInfoUpdate } from '../types/Navigation/NavInfoUpdate';
import { DisplayNameUpdate } from '../types/Patient/DisplayNameUpdate';
import { FinalUpdate } from '../types/Patient/FinalUpdate';
import { TextPrefs } from '../types/Patient/TextPrefs';

const updateUserDoc = async (
  payload: ContactEmailUpdate | EnrollmentInfo | NavInfoUpdate | DisplayNameUpdate | FinalUpdate | TextPrefs,
) => {
  let data;
  const docRef = doc(db, 'users', payload.patientId);

  // HANDLE ContactEmailUpdate UPDATES
  function isContactEmailUpdate(
    payload: ContactEmailUpdate | EnrollmentInfo | NavInfoUpdate | DisplayNameUpdate | FinalUpdate | TextPrefs,
  ): payload is ContactEmailUpdate {
    return (payload as ContactEmailUpdate).contactEmail !== undefined;
  }
  if (isContactEmailUpdate(payload)) {
    data = {
      contactEmail: payload.contactEmail,
    };
    await setDoc(docRef, data, { merge: true });
  }

  // HANDLE EnrollmentInfo UPDATES
  function isEnrollmentInfo(
    payload: ContactEmailUpdate | EnrollmentInfo | NavInfoUpdate | DisplayNameUpdate | FinalUpdate | TextPrefs,
  ): payload is EnrollmentInfo {
    return (payload as EnrollmentInfo).campaignId !== undefined;
  }
  if (isEnrollmentInfo(payload) && payload.campaignId) {
    data = {
      enrollmentInfo: {
        campaignId: payload.campaignId,
        patientId: payload.patientId,
        choice: payload.choice,
        enrollmentEmailSent: payload.enrollmentEmailSent,
        choiceMade: payload.choiceMade ? payload.choiceMade : '',
        appointment: payload.appointment ? payload.appointment : '',
        completedAppointmentOn: payload.completedAppointmentOn ? payload.completedAppointmentOn : '',
        completedSelfOn: payload.completedSelfOn ? payload.completedSelfOn : '',
      },
    };
    await setDoc(docRef, data, { merge: true });
  }

  // HANDLE DisplayName UPDATES
  function isDisplayNameUpdate(
    payload: ContactEmailUpdate | EnrollmentInfo | NavInfoUpdate | DisplayNameUpdate | FinalUpdate | TextPrefs,
  ): payload is DisplayNameUpdate {
    return (payload as DisplayNameUpdate).displayName !== undefined;
  }
  if (isDisplayNameUpdate(payload) && payload.displayName) {
    data = {
      displayName: payload.displayName,
    };
    await setDoc(docRef, data, { merge: true });
  }

  // HANDLE NavInfo UPDATES
  function isNavInfo(
    payload: ContactEmailUpdate | EnrollmentInfo | NavInfoUpdate | DisplayNameUpdate | FinalUpdate | TextPrefs,
  ): payload is NavInfoUpdate {
    return (payload as NavInfoUpdate).navInfo !== undefined;
  }
  if (isNavInfo(payload) && payload.navInfo) {
    data = {
      navInfo: payload.navInfo,
    };
    await setDoc(docRef, data, { merge: true });
    return payload.navInfo;
  }

  // HANDLE FilterOptions UPDATES
  function isFinalUpdate(
    payload: ContactEmailUpdate | EnrollmentInfo | NavInfoUpdate | DisplayNameUpdate | FinalUpdate | TextPrefs,
  ): payload is FinalUpdate {
    return (payload as FinalUpdate) !== undefined;
  }
  if (isFinalUpdate(payload) && payload.data) {
    data = payload.data;
    await setDoc(docRef, data, { merge: true });
    return payload;
  }
  // HANDLE TextPrefs UPDATES
  function isTextPrefsUpdate(
    payload: ContactEmailUpdate | EnrollmentInfo | NavInfoUpdate | DisplayNameUpdate | FinalUpdate | TextPrefs,
  ): payload is TextPrefs {
    // TODO: INVESTIGATE SENTRY ERROR HERE: https://mideo.sentry.io/issues/6531833798/?alert_rule_id=15702719&alert_timestamp=1744211609231&alert_type=email&environment=production&notification_uuid=e33bafba-b04d-4753-ba06-bc7088af1d27&project=4508558575992832&referrer=alert_email
    // CAN BE FIXED BY ADDING A CHECK FOR payload.data OR OPTIONAL CHAINING

    return (payload as TextPrefs).data?.canText !== undefined;
  }
  if (isTextPrefsUpdate(payload) && payload.data) {
    // data = payload.data;
    await setDoc(docRef, payload.data, { merge: true });
    return payload;
  }
};

export const useUpdateUserDoc = () => {
  return useMutation({
    mutationFn: (
      payload: ContactEmailUpdate | EnrollmentInfo | NavInfoUpdate | DisplayNameUpdate | FinalUpdate | TextPrefs,
    ) => updateUserDoc(payload),
  });
};
